import { FC, ReactNode } from 'react'
import { Field } from 'formik'
import { StyleFormWrapper, StyleLabel, StyleErrorMessage } from '../style'
import { StyleCheckBox, StyleCheckBoxWrapper } from './style'
import theme from '@utils/theme'

interface TagLinkProps {
  text: string
  onClick: () => void
  style?: string
}

interface CheckboxProps {
  label?: string | ReactNode
  name: string
  styleLabel?: string
  styleWrapper?: string
  tagLinks?: TagLinkProps[]
}

const Checkbox: FC<CheckboxProps> = ({
  label,
  name,
  styleLabel,
  styleWrapper,
  tagLinks = [],
}) => {
  const handleChange = ({ form, value }: any) => {
    form.setFieldValue(name, value)
  }

  // Function to render label with tagLinks
  const renderLabelWithLinks = (labelText: string, hasError: boolean) => {
    if (!tagLinks || tagLinks.length === 0) {
      return (
        <StyleLabel hasError={hasError} styleLabel={styleLabel}>
          {labelText}
        </StyleLabel>
      )
    }

    // Split the label text into parts and insert links
    const parts = []
    let remainingText = labelText
    let lastIndex = 0

    tagLinks.forEach((link, index) => {
      const linkPosition = remainingText.indexOf(link.text)

      if (linkPosition !== -1) {
        // Add text before the link
        if (linkPosition > 0) {
          parts.push(
            <span key={`text-${index}`}>
              {remainingText.substring(0, linkPosition)}
            </span>,
          )
        }

        // Add the link
        parts.push(
          <button
            key={`link-${index}`}
            onClick={link.onClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                link.onClick()
              }
            }}
            style={{
              color: theme.colors.primary,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              background: 'transparent',
              border: 'none',
              padding: 0,
              font: 'inherit',
              ...(link.style ? eval(`(${link.style})`) : {}),
            }}
          >
            {link.text}
          </button>,
        )

        // Update remaining text
        remainingText = remainingText.substring(linkPosition + link.text.length)
      }
    })

    // Add any remaining text after the last link
    if (remainingText) {
      parts.push(<span key="text-last">{remainingText}</span>)
    }

    return (
      <StyleLabel hasError={hasError} styleLabel={styleLabel}>
        {parts}
      </StyleLabel>
    )
  }

  return (
    <Field name={name} type="checkbox">
      {({ form, meta }: any) => {
        const hasError = !!meta.touched && !!meta.error

        return (
          <StyleFormWrapper styleWrapper={styleWrapper}>
            <StyleCheckBoxWrapper>
              <StyleCheckBox
                type="checkbox"
                checked={form.values[name]}
                onChange={(event) =>
                  handleChange({ form, value: event.target.checked })
                }
              />
              {label && typeof label === 'string'
                ? renderLabelWithLinks(label, hasError)
                : label && (
                    <StyleLabel hasError={hasError} styleLabel={styleLabel}>
                      {label}
                    </StyleLabel>
                  )}
            </StyleCheckBoxWrapper>

            {!!hasError && <StyleErrorMessage>{meta?.error}</StyleErrorMessage>}
          </StyleFormWrapper>
        )
      }}
    </Field>
  )
}

export default Checkbox
